import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, ArrowUpRight, MessageSquareText, Plus, FileCheck, Clock, 
  CheckCircle, XCircle, AlertTriangle, Send, Filter, Search,
  ChevronRight, ChevronDown, User, Calendar, Paperclip,
  MessageCircle, Eye, MoreVertical, FileText, Package, Bell,
  TrendingUp, Activity, Mail, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import HelpIcon from '@/components/HelpIcon';
import RFISendDialog from '@/components/RFISendDialog';
import AttachPickerModal from '@/components/AttachPickerModal';
import '@/components/submittals/review-sheet.css';

// RS-7b — the firm's own words for the five kinds, as the register prints them.
const REGISTER_KIND_LABELS = {
  shop_drawing: 'Shop drawing', product_data: 'Product data', sample: 'Sample',
  test_report: 'Test report', certification: 'Certification',
};
import RFIThread, { AttachmentChip } from '@/components/RFIThread';
import SubmittalRegister from '@/components/submittals/SubmittalRegister';
import { TrailMast, TrailTabs, TrackerFace } from '@/components/rfis/TrailFrame';
import SubmittalReviewSheet from '@/components/submittals/SubmittalReviewSheet';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RFI_STATUS_CONFIG = {
  open: { label: 'Open', color: 'bg-blue-500', icon: MessageSquareText },
  pending_response: { label: 'Pending Response', color: 'bg-yellow-500', icon: Clock },
  responded: { label: 'Responded', color: 'bg-green-500', icon: CheckCircle },
  closed: { label: 'Closed', color: 'bg-gray-500', icon: CheckCircle },
};

// Ball in Court - Who needs to take action
const BALL_IN_COURT_CONFIG = {
  designer: { label: 'Designer', color: 'bg-purple-500', bgLight: 'bg-purple-500/20' },
  contractor: { label: 'Contractor', color: 'bg-blue-500', bgLight: 'bg-blue-500/20' },
  owner: { label: 'Owner', color: 'bg-green-500', bgLight: 'bg-green-500/20' },
  architect: { label: 'Architect', color: 'bg-orange-500', bgLight: 'bg-orange-500/20' },
  engineer: { label: 'Engineer', color: 'bg-cyan-500', bgLight: 'bg-cyan-500/20' },
  vendor: { label: 'Vendor', color: 'bg-pink-500', bgLight: 'bg-pink-500/20' },
};

const SUBMITTAL_STATUS_CONFIG = {
  submitted: { label: 'Submitted', color: 'bg-blue-500', icon: Send },
  under_review: { label: 'Under Review', color: 'bg-yellow-500', icon: Eye },
  approved: { label: 'Approved', color: 'bg-green-500', icon: CheckCircle },
  approved_as_noted: { label: 'Approved as Noted', color: 'bg-lime-500', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-500', icon: XCircle },
  resubmit: { label: 'Resubmit Required', color: 'bg-orange-500', icon: AlertTriangle },
  // RS-7b — paper the firm issued for a fabricator to confirm.
  issued: { label: 'Sent for confirmation', color: 'bg-yellow-500', icon: Send },
  reissued: { label: 'Sent for confirmation', color: 'bg-yellow-500', icon: Send },
  confirmed: { label: 'Confirmed', color: 'bg-green-500', icon: CheckCircle },
  returned: { label: 'Returned with marks', color: 'bg-orange-500', icon: AlertTriangle },
  accepted_by_both: { label: 'Accepted by both', color: 'bg-green-600', icon: CheckCircle },
};

const PRIORITY_CONFIG = {
  low: { label: 'Low', color: 'text-gray-400' },
  normal: { label: 'Normal', color: 'text-blue-400' },
  high: { label: 'High', color: 'text-orange-400' },
  critical: { label: 'Critical', color: 'text-red-400' },
};

export default function RFISubmittalPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  // Dark mode state - sync with system preference
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('keyMadeDarkMode') === 'true';
    }
    return false;
  });
  
  const [activeTab, setActiveTab] = useState('tracker');
  const [rfis, setRfis] = useState([]);
  const [submittals, setSubmittals] = useState([]);
  const [rfiSummary, setRfiSummary] = useState({});
  const [submittalSummary, setSubmittalSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);

  // Phase A (Feb 2026) — RFI send dialog state. The dialog itself
  // (RFISendDialog) owns recipient editing + dispatch; we just gate which
  // RFI it's bound to.
  const [sendRFI, setSendRFI] = useState(null);
  
  // Tracker data
  const [tracker, setTracker] = useState(null);
  const [trackerMetrics, setTrackerMetrics] = useState({});
  const [trackerAlerts, setTrackerAlerts] = useState({});
  // RS-11b — the Tracker counts register lines beside the questions.
  const [registerLines, setRegisterLines] = useState([]);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [ballInCourtFilter, setBallInCourtFilter] = useState('all');
  
  // Listen for dark mode changes from other components
  useEffect(() => {
    const handleStorage = () => {
      setDarkMode(localStorage.getItem('keyMadeDarkMode') === 'true');
    };
    window.addEventListener('storage', handleStorage);
    const interval = setInterval(() => {
      const current = localStorage.getItem('keyMadeDarkMode') === 'true';
      if (current !== darkMode) setDarkMode(current);
    }, 500);
    return () => {
      window.removeEventListener('storage', handleStorage);
      clearInterval(interval);
    };
  }, [darkMode]);
  
  // Create RFI Dialog — Feb 2026 rebuild
  const [showCreateRFI, setShowCreateRFI] = useState(false);
  const [newRFI, setNewRFI] = useState({
    subject: '',
    question: '',
    background: '',
    // Feb 2026 (iter 285) — "From/Raised by" field removed. Author is
    // always the current logged-in user, set server-side from JWT.
    recipients: [],                  // [{contact_id, contractor_id, name, email, status:'pending'}]
    // RS-8 — the choices a vendor can simply pick from on their answer page.
    options: [],
    attachments: [],                 // [RFIAttachment] — first-class body attachments
    drawing_reference: '',
    location: '',
    priority: 'normal',
    cost_impact: false,
    schedule_impact: false,
    due_date: '',
    assigned_to: ''
  });
  // Auxiliary state for the picker in the new RFI dialog.
  const [contactsForPicker, setContactsForPicker] = useState([]);
  const [teamForPicker, setTeamForPicker] = useState([]);
  const [recipientSearchInDialog, setRecipientSearchInDialog] = useState('');
  // Iter 285 — recipient filter chips: 'project' (default), 'favorites', 'trade', 'group', 'all'.
  const [recipientFilter, setRecipientFilter] = useState('project');
  const [recipientTradeFilter, setRecipientTradeFilter] = useState('');
  const [recipientGroupFilter, setRecipientGroupFilter] = useState('');
  const [projectLinkedContactIds, setProjectLinkedContactIds] = useState([]);
  const [contactGroups, setContactGroups] = useState([]);
  // Newly created RFI awaiting an immediate Send (the create→send one-shot flow).
  const [pendingSendRFI, setPendingSendRFI] = useState(null);
  // Attach picker — open state for the New RFI dialog.
  const [attachPickerOpen, setAttachPickerOpen] = useState(false);
  
  // Create Submittal Dialog — Feb 2026 rebuild (information FROM an outside party)
  const [showCreateSubmittal, setShowCreateSubmittal] = useState(false);
  // RS-7b — one form for both directions: paper coming in from a party, and a
  // shop drawing the firm sends out for the fabricator to confirm.
  const BLANK_SUBMITTAL = {
    title: '', note: '', kind: 'product_data', direction: 'received',
    membership_id: '', category_key: '', attachments: [],
    previous_submittal_id: '',
  };
  const [newSubmittal, setNewSubmittal] = useState(BLANK_SUBMITTAL);
  const [parties, setParties] = useState([]);
  const [showAttachPicker, setShowAttachPicker] = useState(false);
  const [submittalSearchInDialog, setSubmittalSearchInDialog] = useState('');
  
  // View/Respond Dialog
  const [showViewRFI, setShowViewRFI] = useState(false);
  const [selectedRFI, setSelectedRFI] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [isFinalResponse, setIsFinalResponse] = useState(false);
  // RS-3b — may this user give the office's final word, and if not, who can?
  const [answerRight, setAnswerRight] = useState({ can_answer: true, names: [] });
  
  // Review Submittal Dialog

  useEffect(() => {
    fetchData();
  }, [projectId]);

  // RS-3b — the firm decides who gives the final answer.
  useEffect(() => {
    axios.get(`${API}/rfi-permissions`)
      .then((r) => setAnswerRight(r.data))
      .catch(() => {});
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [rfisRes, submittalsRes, projectRes, trackerRes, registerRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}/rfis`),
        axios.get(`${API}/projects/${projectId}/submittals`),
        axios.get(`${API}/projects/${projectId}`),
        axios.get(`${API}/projects/${projectId}/rfi-tracker`),
        axios.get(`${API}/projects/${projectId}/submittal-register`).catch(() => ({ data: { lines: [] } }))
      ]);
      
      setRfis(rfisRes.data.rfis || []);
      setRfiSummary(rfisRes.data.summary || {});
      setSubmittals(submittalsRes.data.submittals || []);
      setSubmittalSummary(submittalsRes.data.summary || {});
      // RS-6 — who may put the firm's name to a stamp, and what the paper says.
      setCanStamp(Boolean(submittalsRes.data.can_stamp));
      setStampers(submittalsRes.data.stampers || []);
      setPaper(submittalsRes.data.paper || {});
      // RS-7 — may this user put the stamped copy in front of the client?
      setCanRelease(Boolean(submittalsRes.data.can_release));
      setReleasers(submittalsRes.data.releasers || []);
      setClientName(submittalsRes.data.client_name || 'the client');
      setProject(projectRes.data);
      setTracker(trackerRes.data.tracker || {});
      setTrackerMetrics(trackerRes.data.metrics || {});
      setTrackerAlerts(trackerRes.data.alerts || {});
      setRegisterLines(registerRes.data?.lines || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load RFIs and Submittals');
    } finally {
      setLoading(false);
    }
  };

  // Lazy-load contacts + team for the rebuilt RFI/Submittal dialog pickers.
  //
  // Hardening (Feb 2026 — production bug fix): the contacts endpoint can
  // return either a bare list or `{contacts: [...]}` depending on tenant.
  // Use the same proven `/contacts/list` endpoint + shape-handling that the
  // main Contacts directory uses, and surface failures loudly so silent
  // empty-picker states don't reappear.
  useEffect(() => {
    if (!showCreateRFI && !showCreateSubmittal) return;
    if (contactsForPicker.length === 0) {
      axios.get(`${API}/contacts/list`)
        .then(r => {
          const data = Array.isArray(r.data) ? r.data : (r.data?.contacts || []);
          setContactsForPicker(data);
          if (data.length === 0) {
            // Not an error — but warn so we can spot empty tenants quickly.
            console.warn('[RFISubmittalPage] /contacts/list returned 0 contacts for this tenant');
          }
        })
        .catch(err => {
          console.error('[RFISubmittalPage] Failed to load contacts:', err);
          toast.error('Could not load contacts — try reopening this dialog');
        });
    }
    if (teamForPicker.length === 0) {
      axios.get(`${API}/team/members`)
        .then(r => {
          const members = r.data?.members || (Array.isArray(r.data) ? r.data : []);
          setTeamForPicker(members);
        })
        .catch(err => console.error('[RFISubmittalPage] Failed to load team:', err));
    }
    // Iter 285 — project-linked contact ids for the "Project" filter chip
    // (contacts already used on this project via RFIs / RFPs).
    if (showCreateRFI && projectLinkedContactIds.length === 0) {
      axios.get(`${API}/projects/${projectId}/linked-contact-ids`)
        .then(r => setProjectLinkedContactIds(r.data?.contact_ids || []))
        .catch(err => console.error('[RFISubmittalPage] Failed to load project-linked ids:', err));
    }
    // Contact groups for the "Group" filter chip.
    if (showCreateRFI && contactGroups.length === 0) {
      axios.get(`${API}/contact-groups`)
        .then(r => setContactGroups(r.data?.groups || []))
        .catch(err => console.error('[RFISubmittalPage] Failed to load contact groups:', err));
    }
  }, [showCreateRFI, showCreateSubmittal, projectId]);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateRFI = async () => {
    if (!newRFI.subject || !newRFI.question) {
      toast.error('Subject and Question are required');
      return;
    }
    // Feb 2026 (iter 285) — no author validation; the server sets author
    // from the JWT. Recipients aren't strictly required at save-time
    // (draft RFIs are OK) — they're only required to actually Send.
    try {
      const response = await axios.post(`${API}/projects/${projectId}/rfis`, {
        ...newRFI,
        options: (newRFI.options || []).map(o => o.trim()).filter(Boolean).slice(0, 6),
      });
      const created = response.data.rfi;
      toast.success(`RFI ${created.rfi_number} created`);
      setShowCreateRFI(false);
      setNewRFI({
        subject: '', question: '', background: '',
        recipients: [], attachments: [], options: [],
        drawing_reference: '', location: '', priority: 'normal',
        cost_impact: false, schedule_impact: false, due_date: '', assigned_to: '',
      });
      setRecipientSearchInDialog('');
      setRecipientFilter('project');
      setRecipientTradeFilter('');
      setRecipientGroupFilter('');
      // If recipients were picked, offer to send immediately.
      if ((created.recipients || []).length > 0) {
        setPendingSendRFI({ ...created, _selected_all: true });
      }
      fetchData();
    } catch (error) {
      console.error('Error creating RFI:', error);
      toast.error('Failed to create RFI');
    }
  };

  useEffect(() => {
    if (!projectId) return;
    axios.get(`${API}/projects/${projectId}/memberships`)
      .then((r) => setParties((r.data?.memberships || r.data?.rows || [])
        .map((m) => ({ id: m.id, name: m.company_name || m.name || 'A party',
          trade: m.trade || '' }))))
      .catch(() => setParties([]));
  }, [projectId]);

  const outbound = newSubmittal.direction === 'issued';


  const viewRFI = async (rfi) => {
    try {
      const response = await axios.get(`${API}/rfis/${rfi.id}`);
      setSelectedRFI(response.data);
      setShowViewRFI(true);
    } catch (error) {
      toast.error('Failed to load RFI details');
    }
  };

  const handleRespondToRFI = async () => {
    if (!responseText.trim()) {
      toast.error('Please enter a response');
      return;
    }
    
    try {
      await axios.post(`${API}/rfis/${selectedRFI.id}/respond`, {
        responder_name: 'Designer',
        responder_role: 'designer',
        response_text: responseText,
        is_final: isFinalResponse
      });
      
      toast.success('Response added');
      setResponseText('');
      setIsFinalResponse(false);
      
      // Refresh the RFI
      const response = await axios.get(`${API}/rfis/${selectedRFI.id}`);
      setSelectedRFI(response.data);
      fetchData();
    } catch (error) {
      toast.error('Failed to add response');
    }
  };

  const handleCloseRFI = async () => {
    try {
      await axios.post(`${API}/rfis/${selectedRFI.id}/close`, {
        closed_by: 'Designer',
        final_answer: selectedRFI.final_answer || responseText
      });
      
      toast.success('RFI closed');
      setShowViewRFI(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to close RFI');
    }
  };

  // RS-5 — the desk picks a hand-in up; the stamps themselves are RS-6.
  const [registerKey, setRegisterKey] = useState(0);
  const startReview = async (submittal) => {
    try {
      await axios.patch(`${API}/projects/${projectId}/submittals/${submittal.id}`,
        { status: 'under_review' });
      toast.success(`${submittal.submittal_number} is under review`);
      setRegisterKey((k) => k + 1);
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not start that review');
    }
  };

  // RS-6 — the review sheet: the paper, the thread and the four stamps.
  const [canStamp, setCanStamp] = useState(false);
  const [stampers, setStampers] = useState([]);
  const [paper, setPaper] = useState({});
  const [reviewing, setReviewing] = useState(null);
  const [canRelease, setCanRelease] = useState(false);
  const [releasers, setReleasers] = useState([]);
  const [clientName, setClientName] = useState('the client');

  const openReviewSubmittal = (submittal) => setReviewing(submittal);
  const closeReview = () => setReviewing(null);
  const afterStamp = () => { setRegisterKey((k) => k + 1); fetchData(); };

  const openIssueDialog = (previous) => {
    setNewSubmittal({
      ...BLANK_SUBMITTAL,
      direction: 'issued',
      kind: 'shop_drawing',
      title: previous?.title || '',
      membership_id: previous?.membership_id || '',
      category_key: previous?.for?.category_key || '',
      previous_submittal_id: previous?.id || '',
    });
    setReviewing(null);
    setShowCreateSubmittal(true);
  };

  const handleCreateSubmittal = async () => {
    if (!newSubmittal.title.trim()) { toast.error('Give it a title first'); return; }
    if (!newSubmittal.membership_id) { toast.error('Say which party this is for'); return; }
    if (!newSubmittal.attachments.length) { toast.error('Add the PDF or photos first'); return; }
    try {
      const payload = {
        title: newSubmittal.title.trim(),
        note: newSubmittal.note,
        kind: outbound ? 'shop_drawing' : newSubmittal.kind,
        direction: newSubmittal.direction,
        membership_id: newSubmittal.membership_id,
        for: { category_key: newSubmittal.category_key || null, clip_ids: [], sheet_ids: [] },
        attachments: newSubmittal.attachments,
      };
      if (newSubmittal.previous_submittal_id) {
        payload.previous_submittal_id = newSubmittal.previous_submittal_id;
      }
      const response = await axios.post(`${API}/projects/${projectId}/submittals`, payload);
      toast.success(outbound
        ? `${response.data.submittal.submittal_number} sent for confirmation`
        : `Submittal ${response.data.submittal.submittal_number} logged`);
      setShowCreateSubmittal(false);
      setNewSubmittal(BLANK_SUBMITTAL);
      setRegisterKey((k) => k + 1);
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'That did not save');
    }
  };


  // Calculate days remaining until due date
  const getDaysRemaining = (dueDate) => {
    if (!dueDate) return null;
    const due = new Date(dueDate);
    const now = new Date();
    const diffTime = due - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Determine who has the ball (who needs to take action)
  const getBallInCourt = (rfi) => {
    if (rfi.status === 'closed') return null;
    
    // If last response was from contractor, ball is with designer
    const lastResponse = rfi.responses?.[rfi.responses.length - 1];
    if (lastResponse) {
      // If designer responded last, ball is with contractor/submitter
      if (lastResponse.responder_role === 'designer' || lastResponse.responder_role === 'architect') {
        return rfi.submitted_by_role || 'contractor';
      }
    }
    
    // If no responses yet, ball is with whoever it's assigned to
    if (rfi.assigned_to) {
      // Parse assigned_to to determine role
      if (rfi.assigned_to.toLowerCase().includes('designer')) return 'designer';
      if (rfi.assigned_to.toLowerCase().includes('architect')) return 'architect';
      if (rfi.assigned_to.toLowerCase().includes('owner')) return 'owner';
      if (rfi.assigned_to.toLowerCase().includes('engineer')) return 'engineer';
    }
    
    // Default: ball is with designer to respond
    return 'designer';
  };

  // Format days remaining display
  const formatDaysRemaining = (days) => {
    if (days === null) return null;
    if (days < 0) return { text: `${Math.abs(days)}d overdue`, urgent: true };
    if (days === 0) return { text: 'Due today', urgent: true };
    if (days === 1) return { text: 'Due tomorrow', urgent: true };
    if (days <= 3) return { text: `${days}d left`, urgent: true };
    if (days <= 7) return { text: `${days}d left`, urgent: false };
    return { text: `${days}d left`, urgent: false };
  };

  const handleSendRFIReminder = async (rfi) => {
    // For now, show a toast and log - in production, would open email dialog
    try {
      // The backend endpoint expects an email address
      // For demo, we'll use a placeholder flow
      const assignedEmail = rfi.assigned_to_email || 'contractor@example.com';
      
      await axios.post(`${API}/rfis/${rfi.id}/send-reminder`, {
        email: assignedEmail,
        message: `Please respond to RFI ${rfi.rfi_number}: ${rfi.subject}`
      });
      
      toast.success(`Reminder sent for ${rfi.rfi_number}`);
    } catch (error) {
      console.error('Error sending reminder:', error);
      // Still show success for demo purposes since email may not be configured
      toast.info(`Reminder queued for ${rfi.rfi_number} (email delivery depends on SendGrid config)`);
    }
  };

  const getStatusConfig = (status, type) => {
    if (type === 'rfi') return RFI_STATUS_CONFIG[status] || RFI_STATUS_CONFIG.open;
    return SUBMITTAL_STATUS_CONFIG[status] || SUBMITTAL_STATUS_CONFIG.submitted;
  };

  const filteredRfis = rfis.filter(r => {
    const matchesSearch = !searchQuery || 
      r.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.rfi_number.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || r.status === statusFilter;
    const matchesBallInCourt = ballInCourtFilter === 'all' || getBallInCourt(r) === ballInCourtFilter;
    return matchesSearch && matchesStatus && matchesBallInCourt;
  });

  const filteredSubmittals = submittals.filter(s => {
    const matchesSearch = !searchQuery || 
      s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.submittal_number.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || s.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="ktr-page" data-testid="rfi-submittal-page">
      {/* RS-11b — the masthead, in the Site Office vocabulary. */}
      <div className="ktr-wrap">
        <TrailMast
          projectName={project?.name}
          questionCount={rfiSummary.total || 0}
          submittalCount={submittalSummary.total || 0}
          onBack={() => navigate(projectId ? `/projects/${projectId}/hub` : '/')}
          onNewQuestion={() => setShowCreateRFI(true)}
          onNewSubmittal={() => setShowCreateSubmittal(true)}
        />
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="ktr-tabrow">
                <TrailTabs value={activeTab} onChange={setActiveTab}
                  questionsOpen={rfiSummary.open || 0}
                  submittalsOpen={registerLines.filter((l) => !['approved','approved_as_noted','rejected','released','accepted','accepted_by_both','spec_stands'].includes(l.state)).length} />

              <div className="sp">
                {activeTab === 'rfis' && (
                  <>
                    <div className="relative w-64">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                      <Input
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 bg-[#1A1A1A] border-[#2A2A2A] text-white"
                      />
                    </div>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-40 bg-[#1A1A1A] border-[#2A2A2A] text-white">
                        <SelectValue placeholder="All Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        {activeTab === 'rfis' ? (
                          <>
                            <SelectItem value="open">Open</SelectItem>
                            <SelectItem value="pending_response">Pending Response</SelectItem>
                            <SelectItem value="responded">Responded</SelectItem>
                            <SelectItem value="closed">Closed</SelectItem>
                          </>
                        ) : (
                          <>
                            <SelectItem value="submitted">Submitted</SelectItem>
                            <SelectItem value="under_review">Under Review</SelectItem>
                            <SelectItem value="approved">Approved</SelectItem>
                            <SelectItem value="approved_as_noted">Approved as Noted</SelectItem>
                            <SelectItem value="rejected">Rejected</SelectItem>
                            <SelectItem value="resubmit">Resubmit Required</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                    
                    {/* Ball in Court Filter - RFIs only */}
                    {activeTab === 'rfis' && (
                      <Select value={ballInCourtFilter} onValueChange={setBallInCourtFilter}>
                        <SelectTrigger className={`w-40 ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A] text-white' : 'bg-white border-[#E5E2DD]'}`}>
                          <SelectValue placeholder="Ball in Court" />
                        </SelectTrigger>
                        <SelectContent className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
                          <SelectItem value="all" className={darkMode ? 'text-white' : ''}>All Parties</SelectItem>
                          <SelectItem value="designer" className={darkMode ? 'text-white' : ''}>
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                              Designer
                            </span>
                          </SelectItem>
                          <SelectItem value="contractor" className={darkMode ? 'text-white' : ''}>
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                              Contractor
                            </span>
                          </SelectItem>
                          <SelectItem value="owner" className={darkMode ? 'text-white' : ''}>
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-green-500"></span>
                              Owner
                            </span>
                          </SelectItem>
                          <SelectItem value="architect" className={darkMode ? 'text-white' : ''}>
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                              Architect
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Tracker Tab */}
            <TabsContent value="tracker" className="mt-0">
              <TrackerFace
                rfis={rfis}
                lines={registerLines}
                metrics={trackerMetrics}
                ballOf={getBallInCourt}
                onOpenQuestion={viewRFI}
                onOpenLine={(line) => {
                  const s = submittals.find((x) => x.id === line.submittal_id);
                  if (s) openReviewSubmittal(s); else setActiveTab('submittals');
                }}
              />
            </TabsContent>

            {/* RFIs Tab */}
            <TabsContent value="rfis" className="mt-0">
              <div className="py-6">
                {loading ? (
                  <div className="text-center py-12">
                    <p className="text-gray-500">Loading RFIs...</p>
                  </div>
                ) : filteredRfis.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#1A1A1A] mb-4">
                      <MessageSquareText className="w-8 h-8 text-gray-600" />
                    </div>
                    <h3 className="text-lg text-white mb-2">No RFIs found</h3>
                    <p className="text-gray-500 text-sm">Create an RFI to start tracking design clarifications</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredRfis.map(rfi => {
                      const statusConfig = getStatusConfig(rfi.status, 'rfi');
                      const priorityConfig = PRIORITY_CONFIG[rfi.priority] || PRIORITY_CONFIG.normal;
                      const ballInCourt = getBallInCourt(rfi);
                      const ballConfig = ballInCourt ? BALL_IN_COURT_CONFIG[ballInCourt] : null;
                      const daysRemaining = getDaysRemaining(rfi.due_date);
                      const daysDisplay = formatDaysRemaining(daysRemaining);
                      
                      return (
                        <div
                          key={rfi.id}
                          className={`p-4 rounded-xl border transition-colors cursor-pointer ${
                            darkMode 
                              ? 'border-[#1A1A1A] bg-[#0D0D0D] hover:bg-[#1A1A1A]/30' 
                              : 'border-[#E5E2DD] bg-white hover:bg-[#FAF9F7]'
                          }`}
                          onClick={() => viewRFI(rfi)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2 flex-wrap">
                                <span className="font-mono text-sm kmr-key">{rfi.rfi_number}</span>
                                <Badge className={`${statusConfig.color} text-white text-xs`}>
                                  {statusConfig.label}
                                </Badge>
                                <span className={`text-xs ${priorityConfig.color}`}>
                                  {priorityConfig.label}
                                </span>
                                {/* Ball in Court Badge */}
                                {ballConfig && rfi.status !== 'closed' && (
                                  <Badge className={`${ballConfig.bgLight} ${ballConfig.color.replace('bg-', 'text-')} text-xs border-none`}>
                                    ● Ball: {ballConfig.label}
                                  </Badge>
                                )}
                                {/* Days Remaining */}
                                {daysDisplay && rfi.status !== 'closed' && (
                                  <span className={`text-xs flex items-center gap-1 ${daysDisplay.urgent ? 'text-red-400' : 'text-gray-500'}`}>
                                    <Clock className="w-3 h-3" />
                                    {daysDisplay.text}
                                  </span>
                                )}
                                {rfi.cost_impact && (
                                  <Badge variant="outline" className="text-yellow-500 border-yellow-500 text-xs">
                                    $ Impact
                                  </Badge>
                                )}
                                {rfi.schedule_impact && (
                                  <Badge variant="outline" className="text-orange-500 border-orange-500 text-xs">
                                    Schedule Impact
                                  </Badge>
                                )}
                              </div>
                              <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-[#1A1918]'}`}>{rfi.subject}</h3>
                              <p className={`text-sm mt-1 line-clamp-2 ${darkMode ? 'text-gray-500' : 'text-[#6B6965]'}`}>{rfi.question}</p>
                              <div className={`flex items-center gap-4 mt-3 text-xs ${darkMode ? 'text-gray-500' : 'text-[#6B6965]'}`}>
                                <span className="flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {rfi.raised_by_name || rfi.submitted_by}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {new Date(rfi.submitted_at).toLocaleDateString()}
                                </span>
                                {rfi.responses?.length > 0 && (
                                  <span className="flex items-center gap-1 text-green-500">
                                    <MessageCircle className="w-3 h-3" />
                                    {rfi.responses.length} response(s)
                                  </span>
                                )}
                                {rfi.drawing_reference && !rfi.sheet_mark?.drawing_id && (
                                  <span className="flex items-center gap-1">
                                    <FileText className="w-3 h-3" />
                                    {rfi.drawing_reference}
                                  </span>
                                )}
                                {/* RS-1 — raised by a note on a sheet: go to the note. */}
                                {rfi.sheet_mark?.drawing_id && (
                                  <a
                                    href={`/projects/${projectId}/drawings?drawing=${rfi.sheet_mark.drawing_id}&version=${rfi.sheet_mark.version_id || ''}`}
                                    onClick={(e) => e.stopPropagation()}
                                    className="font-mono text-[11px] hover:underline"
                                    data-testid={`rfi-sheet-${rfi.id}`}
                                  >
                                    {rfi.drawing_reference
                                      || `${rfi.drawing_number || rfi.linked_drawing_number || 'sheet'} · note`}
                                  </a>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 mt-1">
                              {/* Phase A — Send button per RFI. Hidden on
                                  closed RFIs. Click stops propagation so the
                                  row's view-detail handler doesn't fire. */}
                              {rfi.status !== 'closed' && (
                                <Button
                                  data-testid={`rfi-send-btn-${rfi.id}`}
                                  size="sm"
                                  variant="ghost"
                                  className="text-[#70170F] hover:bg-[#70170F]/10"
                                  onClick={(e) => { e.stopPropagation(); setSendRFI(rfi); }}
                                  title="Send this RFI to one or more recipients"
                                >
                                  <Send className="w-4 h-4" />
                                </Button>
                              )}
                              <ChevronRight className={`w-5 h-5 ${darkMode ? 'text-gray-500' : 'text-[#6B6965]'}`} />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Submittals Tab */}
            <TabsContent value="submittals" className="mt-0">
              {/* RS-4 — the one register; RS-11b — Open opens the review sheet. */}
              <SubmittalRegister projectId={projectId} reloadKey={registerKey}
                submittals={submittals} onOpen={openReviewSubmittal} />
            </TabsContent>
          </Tabs>
      </div>

      {/* Create RFI Dialog — Feb 2026 rebuild */}
      <Dialog open={showCreateRFI} onOpenChange={setShowCreateRFI}>
        <DialogContent
          data-testid="rfi-create-dialog"
          className="sm:max-w-2xl kmr-panel max-h-[90vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquareText className="w-5 h-5 kmr-key" />
              New Request for Information
            </DialogTitle>
            <DialogDescription className="text-gray-500">
              An RFI is a question sent to one or more recipients connected to the project.
              You&apos;ll be able to send it via email immediately after saving.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-gray-300">Subject *</Label>
              <Input
                data-testid="rfi-create-subject"
                placeholder="e.g., Kitchen cabinet height clarification"
                value={newRFI.subject}
                onChange={(e) => setNewRFI(prev => ({ ...prev, subject: e.target.value }))}
                className="mt-1 bg-[#1A1A1A] border-[#2A2A2A] text-white"
              />
            </div>
            <div>
              <Label className="text-gray-300">Question *</Label>
              <Textarea
                data-testid="rfi-create-question"
                placeholder="What specific clarification do you need?"
                value={newRFI.question}
                onChange={(e) => setNewRFI(prev => ({ ...prev, question: e.target.value }))}
                className="mt-1 bg-[#1A1A1A] border-[#2A2A2A] text-white"
                rows={3}
              />
            </div>
            <div>
              <Label className="text-gray-300">Background / Context</Label>
              <Textarea
                placeholder="Additional context that might help the recipient answer..."
                value={newRFI.background}
                onChange={(e) => setNewRFI(prev => ({ ...prev, background: e.target.value }))}
                className="mt-1 bg-[#1A1A1A] border-[#2A2A2A] text-white"
                rows={2}
              />
            </div>

            {/* Iter 285 — "From/Raised by" removed. Author = current
                logged-in user (set server-side from JWT). */}

            {/* Send to (recipients) — required to actually send. Multi-select from contacts. */}
            <div className="rounded border border-[#2A2A2A] p-3 space-y-2">
              <Label className="text-gray-300 font-semibold">Send to (recipients)</Label>
              <p className="text-xs text-gray-500">Pick one or more recipients to email. You can send immediately after save.</p>
              {(newRFI.recipients || []).length === 0 ? (
                <div className="text-xs text-gray-500 italic">No recipients yet.</div>
              ) : (
                <div className="space-y-1">
                  {newRFI.recipients.map(r => (
                    <div
                      key={r.contact_id || r.contractor_id}
                      data-testid={`rfi-create-recipient-row-${r.contact_id || r.contractor_id}`}
                      className="flex items-center justify-between p-2 rounded bg-[#1A1A1A] text-sm"
                    >
                      <div className="min-w-0">
                        <div className="font-medium truncate">{r.name}</div>
                        <div className="text-xs text-gray-500 truncate">{r.email}</div>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => setNewRFI(p => ({
                          ...p,
                          recipients: p.recipients.filter(x => (x.contact_id || x.contractor_id) !== (r.contact_id || r.contractor_id)),
                        }))}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <Input
                data-testid="rfi-create-recipient-search"
                placeholder={`Search Contacts to add a recipient…${contactsForPicker.length ? ` (${contactsForPicker.length} available)` : ''}`}
                value={recipientSearchInDialog}
                onChange={(e) => setRecipientSearchInDialog(e.target.value)}
                className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
              />

              {/* Iter 285 — filter chips so the picker scales. Typing
                  still searches across all; chips narrow the set.
                  Default: 'project' (project-linked contacts) so the
                  most common recipients on an RFI surface first. */}
              <div className="flex flex-wrap gap-1 items-center" data-testid="rfi-create-recipient-filters">
                {[
                  { key: 'project',   label: `Project${projectLinkedContactIds.length ? ` (${projectLinkedContactIds.length})` : ''}` },
                  { key: 'favorites', label: 'Favorites' },
                  { key: 'trade',     label: 'Trade' },
                  { key: 'group',     label: 'Group' },
                  { key: 'all',       label: 'All' },
                ].map(chip => (
                  <Button
                    key={chip.key}
                    type="button"
                    data-testid={`rfi-create-recipient-filter-${chip.key}`}
                    variant={recipientFilter === chip.key ? 'default' : 'outline'}
                    size="sm"
                    className={`h-7 text-xs ${recipientFilter === chip.key ? 'bg-[#70170F]' : 'border-[#2A2A2A]'}`}
                    onClick={() => {
                      setRecipientFilter(chip.key);
                      if (chip.key !== 'trade') setRecipientTradeFilter('');
                      if (chip.key !== 'group') setRecipientGroupFilter('');
                    }}
                  >
                    {chip.label}
                  </Button>
                ))}
              </div>

              {/* Sub-selector for Trade / Group */}
              {recipientFilter === 'trade' && (
                <Select value={recipientTradeFilter} onValueChange={setRecipientTradeFilter}>
                  <SelectTrigger data-testid="rfi-create-recipient-trade-select" className="bg-[#1A1A1A] border-[#2A2A2A] text-white h-8 text-xs">
                    <SelectValue placeholder="Pick a trade…" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A] text-white">
                    {Array.from(new Set(contactsForPicker.map(c => c.trade).filter(Boolean))).sort().map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {recipientFilter === 'group' && (
                <Select value={recipientGroupFilter} onValueChange={setRecipientGroupFilter}>
                  <SelectTrigger data-testid="rfi-create-recipient-group-select" className="bg-[#1A1A1A] border-[#2A2A2A] text-white h-8 text-xs">
                    <SelectValue placeholder="Pick a group…" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A] text-white">
                    {contactGroups.map(g => (
                      <SelectItem key={g.id} value={g.id}>{g.name} ({g.type})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {/* Feb 2026 (bug fix): the recipient dropdown used to render
                  only when the user had typed something — so anyone who
                  didn't know to search saw a permanently empty picker
                  and reported the RFI form as broken. Now the dropdown
                  is always visible when contacts exist; typing filters. */}
              {contactsForPicker.length > 0 && (
                <div className="max-h-40 overflow-y-auto border border-[#2A2A2A] rounded" data-testid="rfi-create-recipient-list">
                  {(() => {
                    const q = (recipientSearchInDialog || '').toLowerCase();
                    const projectSet = new Set(projectLinkedContactIds);
                    return (contactsForPicker || [])
                      .filter(c => {
                        // Base filters
                        const inList = (newRFI.recipients || []).some(r => (r.contact_id || r.contractor_id) === c.id);
                        if (inList) return false;
                        const hay = `${c.first_name||''} ${c.last_name||''} ${c.company||''} ${c.email||''}`.toLowerCase();
                        if (q && !hay.includes(q)) return false;
                        // Chip filter
                        if (recipientFilter === 'project') return projectSet.has(c.id);
                        if (recipientFilter === 'favorites') return !!c.is_favorite_vendor;
                        if (recipientFilter === 'trade') return !recipientTradeFilter || c.trade === recipientTradeFilter;
                        if (recipientFilter === 'group') {
                          if (!recipientGroupFilter) return false;
                          const gids = c.group_ids || (c.group_id ? [c.group_id] : []);
                          return gids.includes(recipientGroupFilter);
                        }
                        return true; // 'all'
                      })
                      .sort((a, b) => {
                        // Project-linked contacts float to the top even in 'all' view.
                        const aProj = projectSet.has(a.id) ? 0 : 1;
                        const bProj = projectSet.has(b.id) ? 0 : 1;
                        if (aProj !== bProj) return aProj - bProj;
                        const an = `${a.first_name||''} ${a.last_name||''}`.trim().toLowerCase();
                        const bn = `${b.first_name||''} ${b.last_name||''}`.trim().toLowerCase();
                        return an.localeCompare(bn);
                      })
                      .slice(0, 30)
                      .map(c => {
                        const display = `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.company || c.email;
                        const isProjectLinked = projectSet.has(c.id);
                        return (
                          <button
                            key={c.id}
                            type="button"
                            data-testid={`rfi-create-recipient-option-${c.id}`}
                            className="w-full text-left p-2 hover:bg-[#1A1A1A] text-sm flex items-center justify-between gap-2"
                            onClick={() => {
                              if (!c.email) { toast.error('Contact has no email — add one in Contacts first.'); return; }
                              setNewRFI(p => ({
                                ...p,
                                recipients: [
                                  ...(p.recipients || []),
                                  { contact_id: c.id, contractor_id: c.id, name: display, email: c.email, status: 'pending' },
                                ],
                              }));
                              setRecipientSearchInDialog('');
                            }}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="font-medium truncate flex items-center gap-1">
                                {display}
                                {isProjectLinked && (
                                  <span className="text-[9px] uppercase tracking-wider px-1 py-0.5 rounded bg-[#70170F]/20 kmr-key">on project</span>
                                )}
                                {c.is_favorite_vendor && (
                                  <span className="text-[9px] text-yellow-400">★</span>
                                )}
                              </div>
                              <div className="text-xs text-gray-500 truncate">{c.email || 'no email'}{c.company ? ` · ${c.company}` : ''}{c.trade ? ` · ${c.trade}` : ''}</div>
                            </div>
                          </button>
                        );
                      });
                  })()}
                  {/* Empty-state hint when chip filter yields nothing */}
                  {(() => {
                    const projectSet = new Set(projectLinkedContactIds);
                    const q = (recipientSearchInDialog || '').toLowerCase();
                    const visible = (contactsForPicker || []).filter(c => {
                      const inList = (newRFI.recipients || []).some(r => (r.contact_id || r.contractor_id) === c.id);
                      if (inList) return false;
                      const hay = `${c.first_name||''} ${c.last_name||''} ${c.company||''} ${c.email||''}`.toLowerCase();
                      if (q && !hay.includes(q)) return false;
                      if (recipientFilter === 'project') return projectSet.has(c.id);
                      if (recipientFilter === 'favorites') return !!c.is_favorite_vendor;
                      if (recipientFilter === 'trade') return !recipientTradeFilter || c.trade === recipientTradeFilter;
                      if (recipientFilter === 'group') {
                        if (!recipientGroupFilter) return false;
                        const gids = c.group_ids || (c.group_id ? [c.group_id] : []);
                        return gids.includes(recipientGroupFilter);
                      }
                      return true;
                    });
                    if (visible.length === 0) {
                      return (
                        <div className="p-3 text-xs text-gray-500 italic text-center" data-testid="rfi-create-recipient-empty">
                          {recipientFilter === 'project' && projectLinkedContactIds.length === 0
                            ? 'No contacts linked to this project yet — try "All" or another filter.'
                            : 'No matches. Try another filter or clear your search.'}
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}
            </div>

            {/* Attachments — first-class on the RFI body (Feb 2026 — Thread
                redesign). Pulls from Clips / Drawings / Documents / Upload.
                Reciprocal links auto-write so each artifact carries this RFI's id. */}
            <div className="rounded border border-[#2A2A2A] p-3 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-gray-300 font-semibold">Attachments</Label>
                <Button
                  type="button"
                  data-testid="rfi-create-open-attach-picker"
                  variant="outline"
                  size="sm"
                  onClick={() => setAttachPickerOpen(true)}
                  className="border-[#2A2A2A]"
                >
                  <Paperclip className="w-4 h-4 mr-1" />
                  Attach clips, drawings, docs, photos
                </Button>
              </div>
              {newRFI.attachments.length === 0 ? (
                <div className="text-xs text-gray-500 italic">
                  Nothing attached yet. RFIs land best when the recipient can see the drawing/clip you mean.
                </div>
              ) : (
                <div className="flex flex-wrap gap-2" data-testid="rfi-create-attachments-strip">
                  {newRFI.attachments.map(a => (
                    <AttachmentChip
                      key={a.ref_id}
                      attachment={a}
                      onRemove={() => setNewRFI(p => ({
                        ...p, attachments: p.attachments.filter(x => x.ref_id !== a.ref_id),
                      }))}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* RS-8 — "pick one": the answer a vendor can give with one tap. */}
            <div data-testid="rfi-create-options">
              <Label className="text-gray-300">Give them choices (optional)</Label>
              <p className="text-xs text-gray-500 mt-1">
                Up to six. The vendor picks one on their own answer page.
              </p>
              <div className="mt-2 space-y-2">
                {(newRFI.options || []).map((option, i) => (
                  <div className="flex items-center gap-2" key={`option-${i}`}>
                    <Input
                      value={option}
                      placeholder={`Choice ${i + 1}`}
                      maxLength={120}
                      onChange={(e) => setNewRFI(prev => ({
                        ...prev,
                        options: prev.options.map((o, j) => (j === i ? e.target.value : o)),
                      }))}
                      data-testid={`rfi-create-option-${i}`}
                      className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setNewRFI(prev => ({
                        ...prev, options: prev.options.filter((_, j) => j !== i),
                      }))}
                      data-testid={`rfi-create-option-remove-${i}`}
                      aria-label="Remove this choice"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                {(newRFI.options || []).length < 6 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setNewRFI(prev => ({
                      ...prev, options: [...(prev.options || []), ''],
                    }))}
                    data-testid="rfi-create-option-add"
                    className="border-gray-600 text-gray-200 bg-transparent hover:bg-[#1A1A1A]"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add a choice
                  </Button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Drawing Reference</Label>
                <Input
                  placeholder="e.g., A-101, Detail 3"
                  value={newRFI.drawing_reference}
                  onChange={(e) => setNewRFI(prev => ({ ...prev, drawing_reference: e.target.value }))}
                  className="mt-1 bg-[#1A1A1A] border-[#2A2A2A] text-white"
                />
              </div>
              <div>
                <Label className="text-gray-300">Location</Label>
                <Input
                  placeholder="e.g., Kitchen, East Wall"
                  value={newRFI.location}
                  onChange={(e) => setNewRFI(prev => ({ ...prev, location: e.target.value }))}
                  className="mt-1 bg-[#1A1A1A] border-[#2A2A2A] text-white"
                />
              </div>
            </div>
            <div>
              <Label className="text-gray-300">Priority</Label>
              <Select
                value={newRFI.priority}
                onValueChange={(v) => setNewRFI(prev => ({ ...prev, priority: v }))}
              >
                <SelectTrigger className="mt-1 bg-[#1A1A1A] border-[#2A2A2A] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newRFI.cost_impact}
                  onChange={(e) => setNewRFI(prev => ({ ...prev, cost_impact: e.target.checked }))}
                  className="rounded border-gray-600"
                />
                <span className="text-sm text-gray-300">Potential Cost Impact</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newRFI.schedule_impact}
                  onChange={(e) => setNewRFI(prev => ({ ...prev, schedule_impact: e.target.checked }))}
                  className="rounded border-gray-600"
                />
                <span className="text-sm text-gray-300">Potential Schedule Impact</span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateRFI(false)} className="border-[#2A2A2A]">
              Cancel
            </Button>
            <Button
              data-testid="rfi-create-submit"
              onClick={handleCreateRFI}
              className="kmr-go"
            >
              {(newRFI.recipients || []).length > 0 ? 'Create & continue to Send' : 'Save as draft'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RS-7b — New Submittal: both directions, one route. */}
      <Dialog open={showCreateSubmittal} onOpenChange={setShowCreateSubmittal}>
        <DialogContent className="sm:max-w-lg kmr-panel">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 kmr-key" />
              {outbound ? 'Issue a shop drawing' : 'New submittal'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-2" data-testid="submittal-direction">
              {[['received', 'Coming in from a party'],
                ['issued', 'Going out for fabrication']].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setNewSubmittal(p => ({ ...p, direction: key,
                    kind: key === 'issued' ? 'shop_drawing' : p.kind }))}
                  aria-pressed={newSubmittal.direction === key}
                  data-testid={`submittal-direction-${key}`}
                  className={`kmr-pick rounded border p-3 text-left text-sm${
                    newSubmittal.direction === key ? ' kmr-pick--on' : ''}`}
                >
                  {label}
                </button>
              ))}
            </div>
            {outbound && (
              <p className="text-sm kmr-dim" data-testid="submittal-issue-copy">
                {`A shop drawing ${paper.firm || 'the firm'} drew. The party confirms it can be built as drawn before it is final.`}
              </p>
            )}

            <div>
              <Label>Party *</Label>
              <select
                value={newSubmittal.membership_id}
                onChange={(e) => setNewSubmittal(p => ({ ...p, membership_id: e.target.value }))}
                data-testid="submittal-party"
                className="kmr-field mt-1 w-full rounded border p-2 text-sm"
              >
                <option value="">Choose the party</option>
                {parties.map(party => (
                  <option key={party.id} value={party.id}>
                    {party.trade ? `${party.name} · ${party.trade}` : party.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Kind *</Label>
                <select
                  value={outbound ? 'shop_drawing' : newSubmittal.kind}
                  disabled={outbound}
                  onChange={(e) => setNewSubmittal(p => ({ ...p, kind: e.target.value }))}
                  data-testid="submittal-kind"
                  className="kmr-field mt-1 w-full rounded border p-2 text-sm disabled:opacity-70"
                >
                  {Object.entries(REGISTER_KIND_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>For (spec category)</Label>
                <Input
                  value={newSubmittal.category_key}
                  onChange={(e) => setNewSubmittal(p => ({ ...p,
                    category_key: e.target.value.toUpperCase().slice(0, 6) }))}
                  placeholder="CAB"
                  data-testid="submittal-category"
                  className="kmr-field mt-1"
                />
              </div>
            </div>

            <div>
              <Label>Title *</Label>
              <Input
                value={newSubmittal.title}
                onChange={(e) => setNewSubmittal(p => ({ ...p, title: e.target.value }))}
                placeholder="Island shop drawings"
                data-testid="submittal-title"
                className="kmr-field mt-1"
              />
            </div>
            <div>
              <Label>Note</Label>
              <Textarea
                value={newSubmittal.note}
                onChange={(e) => setNewSubmittal(p => ({ ...p, note: e.target.value }))}
                rows={2}
                data-testid="submittal-note"
                className="kmr-field mt-1"
              />
            </div>

            <div>
              <Label>Files *</Label>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                {newSubmittal.attachments.map((file, i) => (
                  <span key={`${file.url}-${i}`} className="text-xs kmr-dim"
                    data-testid={`submittal-file-${i}`}>
                    {file.name}
                  </span>
                ))}
                <Button type="button" variant="outline" size="sm"
                  onClick={() => setShowAttachPicker(true)}
                  data-testid="submittal-attach"
                  className="kmr-pick">
                  Attach a drawing, a document or a file
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateSubmittal(false)}
              className="kmr-pick" data-testid="submittal-cancel">
              Cancel
            </Button>
            <Button onClick={handleCreateSubmittal} className="kmr-go"
              data-testid="submittal-save">
              {outbound ? 'Send for confirmation' : 'Log the hand-in'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AttachPickerModal
        open={showAttachPicker}
        onOpenChange={setShowAttachPicker}
        projectId={projectId}
        tabs={['drawings', 'documents', 'upload']}
        title="Attach to the submittal"
        description="The sheets, the product data or the photos this submittal is made of."
        alreadyAttachedIds={(newSubmittal.attachments || []).map((a) => a.ref_id)}
        onAttach={(attachment) => setNewSubmittal(p => ({ ...p, attachments: [
          ...p.attachments,
          { name: attachment.label || 'File',
            ref_id: attachment.ref_id,
            url: attachment.file_url || attachment.thumbnail_url || '',
            type: attachment.metadata?.file_type || attachment.kind || '',
            size: attachment.metadata?.size }],
        }))}
      />

      {/* View RFI Dialog */}
      <Dialog open={showViewRFI} onOpenChange={setShowViewRFI}>
        <DialogContent className="sm:max-w-2xl kmr-panel max-h-[90vh] overflow-y-auto">
          {selectedRFI && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <span className="font-mono kmr-key">{selectedRFI.rfi_number}</span>
                  <Badge className={`${getStatusConfig(selectedRFI.status, 'rfi').color} text-white`}>
                    {getStatusConfig(selectedRFI.status, 'rfi').label}
                  </Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <h3 className="font-medium text-white text-lg">{selectedRFI.subject}</h3>
                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                    <span>
                      From: {selectedRFI.raised_by_name || selectedRFI.submitted_by}
                      {selectedRFI.raised_by_type ? (
                        <span className="ml-1 text-xs px-1.5 py-0.5 rounded bg-[#1A1A1A] text-gray-400">
                          {selectedRFI.raised_by_type === 'internal' ? 'internal' : 'external'}
                        </span>
                      ) : (
                        <span className="ml-1 text-xs text-gray-500">({selectedRFI.submitted_by_role})</span>
                      )}
                    </span>
                    <span>{new Date(selectedRFI.submitted_at).toLocaleDateString()}</span>
                  </div>
                </div>
                
                <div className="p-4 rounded-lg bg-[#1A1A1A]">
                  <Label className="text-gray-400 text-xs">Question</Label>
                  <p className="text-white mt-1">{selectedRFI.question}</p>
                  {selectedRFI.background && (
                    <>
                      <Label className="text-gray-400 text-xs mt-3 block">Background</Label>
                      <p className="text-gray-300 mt-1">{selectedRFI.background}</p>
                    </>
                  )}
                </div>

                {/* Body attachments — first-class on the RFI (Feb 2026 — Thread redesign). */}
                {(selectedRFI.attachments || []).length > 0 && (
                  <div data-testid="rfi-detail-attachments">
                    <Label className="text-gray-400 text-xs">Attachments ({selectedRFI.attachments.length})</Label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {selectedRFI.attachments.map(a => (
                        <AttachmentChip key={a.ref_id} attachment={a} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Thread — the conversation (replaces the flat Responses list).
                    Reply box with Attach button is part of the Thread component. */}
                <div>
                  <Label className="text-gray-400 text-xs">Conversation</Label>
                  <div className="mt-2">
                    <RFIThread
                      rfi={selectedRFI}
                      projectId={projectId}
                      currentUserName="You"
                      onUpdated={async () => {
                        // Refetch the RFI so the new message appears.
                        try {
                          const r = await axios.get(`${API}/rfis/${selectedRFI.id}`);
                          setSelectedRFI(r.data);
                          fetchData();
                        } catch (e) {
                          console.error('[RFISubmittalPage] thread refresh failed:', e);
                        }
                      }}
                    />
                  </div>
                </div>
                
                {/* Final Answer */}
                {selectedRFI.final_answer && (
                  <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                    <Label className="text-green-400 text-xs flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      Final Answer
                    </Label>
                    <p className="text-white mt-1">{selectedRFI.final_answer}</p>
                  </div>
                )}
                
                {/* Add Response */}
                {selectedRFI.status !== 'closed' && (
                  <div className="pt-4 border-t border-[#2A2A2A]">
                    <Label className="text-gray-300">Add Response</Label>
                    <Textarea
                      placeholder="Type your response..."
                      value={responseText}
                      onChange={(e) => setResponseText(e.target.value)}
                      className="mt-1 bg-[#1A1A1A] border-[#2A2A2A] text-white"
                      rows={3}
                    />
                    <div className="flex items-center justify-between mt-2">
                      {answerRight.can_answer ? (
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isFinalResponse}
                            onChange={(e) => setIsFinalResponse(e.target.checked)}
                            className="rounded border-gray-600"
                          />
                          <span className="text-sm text-gray-300">This is the final answer</span>
                        </label>
                      ) : (
                        <span className="text-sm text-gray-400" data-testid="rfi-no-answer-perm">
                          {`Waiting for ${answerRight.names.join(', ') || 'the principal designer'} to give the final answer`}
                        </span>
                      )}
                      <Button onClick={handleRespondToRFI} size="sm" className="bg-[#70170F]">
                        <Send className="w-4 h-4 mr-1" />
                        Send Response
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  {selectedRFI.status !== 'closed' && (
                    <>
                      <Button
                        data-testid={`rfi-detail-send-btn-${selectedRFI.id}`}
                        variant="default"
                        onClick={() => { setShowViewRFI(false); setSendRFI(selectedRFI); }}
                        className="kmr-go"
                        size="sm"
                      >
                        <Send className="w-4 h-4 mr-1" />
                        Send
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => handleSendRFIReminder(selectedRFI)}
                        className="border-[#2A2A2A] text-yellow-400 hover:bg-yellow-500/10"
                        size="sm"
                      >
                        <Mail className="w-4 h-4 mr-1" />
                        Send Reminder
                      </Button>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={() => setShowViewRFI(false)} className="border-[#2A2A2A]">
                    Close
                  </Button>
                  {selectedRFI.status !== 'closed' && answerRight.can_answer && (
                    <Button onClick={handleCloseRFI} variant="secondary">
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Close RFI
                    </Button>
                  )}
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Review Submittal Dialog */}
      {/* RS-6 — the review sheet: the paper, the thread and the four stamps. */}
      {reviewing && (
        <SubmittalReviewSheet
          projectId={projectId}
          submittal={reviewing}
          canStamp={canStamp}
          stampers={stampers}
          paper={paper}
          canRelease={canRelease}
          releasers={releasers}
          clientName={clientName}
          onReissue={openIssueDialog}
          onChanged={afterStamp}
          earlier={submittals.find(s => s.id === reviewing.previous_submittal_id) || null}
          onClose={closeReview}
          onStamped={afterStamp}
        />
      )}

      {/* Phase A — RFI Send Dialog. Rendered once at the page root and
          bound to whichever RFI the user clicked Send on. Also used by the
          create→send one-shot flow via pendingSendRFI. */}
      <RFISendDialog
        open={!!sendRFI || !!pendingSendRFI}
        onOpenChange={(v) => { if (!v) { setSendRFI(null); setPendingSendRFI(null); } }}
        rfi={sendRFI || pendingSendRFI}
        onSent={() => { fetchData(); }}
      />

      {/* Unified Attach picker for the New RFI dialog (Feb 2026 — Thread redesign). */}
      <AttachPickerModal
        open={attachPickerOpen}
        onOpenChange={setAttachPickerOpen}
        projectId={projectId}
        alreadyAttachedIds={(newRFI.attachments || []).map(a => a.ref_id)}
        onAttach={(att) => setNewRFI(p => ({
          ...p,
          attachments: [...(p.attachments || []), att],
        }))}
      />
    </div>
  );
}
